<template>
  <AccountCard
    :title="$t('pages.account.settings.sections.delete_account.title')"
    :icon="TrashIcon"
    header-class="bg-[#267476] text-white -mx-6 -mt-5 mb-5 p-[inherit]"
  >
    <h4 class="pt-4 font-bold">
      {{ $t("pages.account.settings.sections.delete_account.note_title") }}
    </h4>
    <ul class="ml-6 mt-4 list-disc p-4 text-primary [&>li>span]:text-[#253754] [&>li]:my-3">
      <li v-for="(note, noteIndex) in $tm('pages.account.settings.sections.delete_account.note_list')" :key="noteIndex">
        <span class="font-semibold">{{ $rt(note.title) }}</span>
        <div class="mt-1 text-[#253754]">
          {{ $rt(note.description) }}
        </div>
      </li>
    </ul>

    <VForm
      :validation-schema="Schema"
      :initial-values="Schema.getDefault()"
      v-slot="{ handleSubmit }"
      as="div"
      class="mb-6 min-w-[15rem] flex-1"
    >
      <form @submit="handleSubmit($event, mutate)" class="max-w-lg">
        <CommonAlert v-if="error" type="error" :content="error.message" class="mb-6 mt-4" />
        <div class="mt-2 w-full">
          <div class="flex flex-row flex-wrap gap-x-2 md:gap-y-6 md:pt-4">
            <BaseVTextInput
              type="password"
              :label="$t('pages.account.settings.sections.password.title')"
              name="password"
              class="flex-1 basis-full"
            />
          </div>

          <div class="mt-8 flex flex-row flex-wrap items-center gap-4">
            <BaseButton
              type="submit"
              class="w-auto max-w-full whitespace-nowrap text-[1rem]"
              :loading="pending"
              size="medium"
              variant="outlined"
            >
              {{ $t("pages.account.buttons.delete_account") }}
            </BaseButton>

            <div class="flex flex-1 shrink-0 basis-1/3 flex-row items-center gap-2 text-primary">
              <div class="flex flex-row items-center gap-2" v-if="success">
                <CheckCircleIcon class="h-[1.5em] w-[1.5em] shrink-0" />
                <span>{{ $t("pages.account.texts.changes_saved") }}</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </VForm>
  </AccountCard>
</template>

<script setup lang="ts">
import { CheckCircleIcon, TrashIcon } from "@heroicons/vue/outline";
import { AxiosError } from "axios";
import * as yup from "yup";
import * as AccountCardModule from "../components/account-card.vue";

// Some tooling setups don't infer a default export from Vue SFCs reliably.
// This keeps both runtime behavior and template usage the same.
const AccountCard = (AccountCardModule as any).default ?? (AccountCardModule as any);

const Schema = yup.object({
  password: yup.string().required(),
});
type Schema = yup.InferType<typeof Schema>;

const error = ref<Error>();
const { mutate, pending, success } = useMutation({
  mutationFn: async (credentials: Schema) =>
    useAxios()
      .delete("/profile/delete", {
        data: credentials,
      })
      .then((res) => res.data),
  onError(err) {
    error.value = err;
    if (err instanceof AxiosError) error.value = new Error(err.response?.data.error);
  },
  onSuccess() {
    error.value = undefined;
    logout();
  },
});
</script>
